import { dataService } from '@rti/shared';
import { htmlToPdfText } from '@/utils/pdfText';

export const DRIVERS_PDF_CACHE_VERSION = 'v11';

export const DRIVERS_POLYMER_ORDER = ['PE', 'PP', 'PS', 'ABS', 'PC', 'PVC', 'PA66', 'PA6', 'PET'];

export const getAbbr = (report: any): string => {
  const id = report?.driverReport || report?.id || '';
  return typeof id === 'string' ? (id.split(' ')[0] || id) : String(id);
};

export const sortReportsByPolymerOrder = (reports: any[]): any[] => {
  const rank = (r: any) => {
    const i = DRIVERS_POLYMER_ORDER.indexOf(getAbbr(r));
    return i === -1 ? DRIVERS_POLYMER_ORDER.length : i;
  };
  return [...reports].sort((a, b) => rank(a) - rank(b));
};

export const formatDateParam = (selectedDate: string): string => {
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(selectedDate);
  if (ymd) return `${ymd[1]}${ymd[2]}${ymd[3]}`;
  const dateObj = new Date(selectedDate);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

export const driversPdfFilename = (selectedDate: string): string =>
  `ResinSmart-Drivers-${formatDateParam(selectedDate)}-${DRIVERS_PDF_CACHE_VERSION}.pdf`;

const formatContentForPDF = (rawText: string): string => {
  return htmlToPdfText(rawText)
    .replace(/\r\n|\r/g, '\n')
    .replace(/\n\n/g, '\n\n')
    .replace(/\r\n\r\n/g, '\n\n')
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '\n')
    .replace(/\x0b/g, '\n')
    .replace(/\r\?/g, '\n-')
    .replace(/\?/g, '-')
    .replace(/\r\r/g, '\n\n')
    .replace(/\r/g, '\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export interface BuildDriversPdfOptions {
  reports: any[];
  selectedDate: string;
  trends?: any[];
  userEmail?: string;
}

export const buildDriversPdf = async ({ reports, selectedDate, trends = [], userEmail = '' }: BuildDriversPdfOptions): Promise<Blob> => {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 7.5;
  const contentWidth = pageWidth - (margin * 3);
  const gutter = 5;

  const bgColor: [number, number, number] = [25, 26, 31];
  const greenColor: [number, number, number] = [124, 252, 0];
  const textColor: [number, number, number] = [222, 231, 217];

  const anchorDate = selectedDate ? new Date(selectedDate) : new Date();
  const driverChartsResponse = await dataService.getDriverChartsData();
  const driverChartsData = driverChartsResponse || [];
  const driverMap: Record<string, any> = {};
  if (Array.isArray(driverChartsData)) {
    for (const d of driverChartsData) {
      if (d && d.driver) driverMap[String(d.driver)] = d;
    }
  }

  const convertValue = (convert: string, price: number) => {
    const metricTonToLb = 2204.62;
    if (convert === 'true') return price / metricTonToLb;
    if (convert === 'reverse') return price * metricTonToLb;
    return price;
  };

  const formatDate = (date: string) => {
    const utcDate = new Date(date + 'T00:00:00Z');
    return utcDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const getLastAvailableDays = (data: any[], numData: number) => {
    const result: any[] = [];
    for (let i = 0; i < numData; i++) {
      const month = anchorDate.getUTCMonth() - i;
      const year = month < 0 ? anchorDate.getUTCFullYear() - 1 : anchorDate.getUTCFullYear();
      const adjustedMonth = (month + 12) % 12;
      const filteredData = data.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate.getUTCFullYear() === year && itemDate.getUTCMonth() === adjustedMonth;
      });
      if (filteredData.length > 0) {
        const lastDay = filteredData.reduce((latest, current) => {
          return new Date(current.date) > new Date(latest.date) ? current : latest;
        });
        result.push(lastDay);
      }
    }
    return result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const orderedReports = sortReportsByPolymerOrder(reports);
  for (let idx = 0; idx < orderedReports.length; idx++) {
    const report = orderedReports[idx];
    if (idx > 0) doc.addPage();

    doc.setFillColor(...bgColor);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    const abbr = getAbbr(report);
    let yPos = margin;

    const ensureSpace = (space: number) => {
      if (yPos + space > pageHeight - margin) {
        doc.addPage();
        doc.setFillColor(...bgColor);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
    };

    const renderSection = (title: string, content: string, maxWidth: number = contentWidth) => {
      if (!content) return;
      ensureSpace(13);
      doc.setTextColor(...greenColor);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'normal');
      doc.text(title, margin, yPos);
      yPos += 6;
      doc.setTextColor(...textColor);
      const text = formatContentForPDF(content);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const lines = text.split('\n').flatMap((paragraph) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return [''];
        const wrapped = doc.splitTextToSize(trimmed, maxWidth);
        return Array.isArray(wrapped) ? wrapped : [wrapped];
      });
      for (const line of lines) {
        ensureSpace(5);
        if (line) doc.text(line, margin, yPos);
        yPos += line ? 4.5 : 2;
      }
      yPos += 4;
    };

    const headerHeight = 16;
    doc.setFillColor(...greenColor);
    doc.setDrawColor(...greenColor);
    const headerY = yPos;

    doc.setTextColor(...greenColor);
    doc.setFontSize(23);
    doc.setFont('helvetica', 'normal');
    doc.text(`${abbr} Drivers`, margin, headerY + 6);

    doc.setTextColor(...textColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${selectedDate || ''}`, margin, yPos + headerHeight - 4);

    try {
      const logoResponse = await fetch('/logos/PrimaryRTi/White.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoUrl = URL.createObjectURL(logoBlob);
        const logoWidth = 75;
        const logoHeight = 16;
        const logoX = pageWidth - margin - logoWidth - 2;
        const logoY = headerY + (headerHeight - logoHeight) / 2;
        doc.addImage(logoUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
        URL.revokeObjectURL(logoUrl);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    }

    yPos += headerHeight + 3.5;
    doc.setDrawColor(...greenColor);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    const polymerTrends: any[] | undefined = trends.length > 0 ? trends[0]?.[abbr] : undefined;
    if (polymerTrends && polymerTrends.length > 0) {
      ensureSpace(20);

      const labelColW = 28;
      const colW = (contentWidth - labelColW) / polymerTrends.length;
      const tableTop = yPos;

      doc.setTextColor(...textColor);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      for (let i = 0; i < polymerTrends.length; i++) {
        doc.text(polymerTrends[i].date, margin + labelColW + colW * i + colW / 2, yPos, { align: 'center' });
      }
      yPos += 3;
      doc.setDrawColor(...textColor);
      doc.setLineWidth(0.1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 4;

      doc.setTextColor(...textColor);
      doc.setFont('helvetica', 'bold');
      doc.text('Price Change', margin, yPos);
      doc.setFont('helvetica', 'normal');
      const aw = 1.2;
      const ah = 1.8;
      const drawArrow = (x: number, up: boolean, color: [number, number, number]) => {
        doc.setFillColor(...color);
        doc.setDrawColor(...color);
        if (up) {
          doc.lines([[aw * 2, 0], [-aw, -ah]], x, yPos, [1, 1], 'F', true);
        } else {
          doc.lines([[aw * 2, 0], [-aw, ah]], x, yPos - ah, [1, 1], 'F', true);
        }
      };
      for (let i = 0; i < polymerTrends.length; i++) {
        const val: string = polymerTrends[i].value || '';
        const isFlatToPos = /flat\s+to\s*\+/i.test(val);
        const isFlatToNeg = /flat\s+to\s*-/i.test(val);
        const isNeg = !isFlatToPos && !isFlatToNeg && (val.startsWith('-') || val.startsWith('('));
        const isFlat = !isFlatToPos && !isFlatToNeg && (!val || val.toLowerCase() === 'flat' || val === '$0.00');
        const textCenterX = margin + labelColW + colW * i + colW / 2;

        if (isFlatToPos || isFlatToNeg) {
          const label = 'Flat to';
          const labelW = doc.getTextWidth(label);
          const gap = 2;
          const totalW = labelW + gap + aw * 2;
          const startX = textCenterX - totalW / 2;
          doc.setTextColor(...textColor);
          doc.text(label, startX, yPos);
          const arrowColor: [number, number, number] = isFlatToPos ? [124, 252, 0] : [232, 91, 76];
          drawArrow(startX + labelW + gap, isFlatToPos, arrowColor);
        } else {
          const valColor: [number, number, number] = isFlat ? textColor : isNeg ? [232, 91, 76] : [124, 252, 0];
          const valText = isNeg ? (val.startsWith('(') ? val.slice(1).replace(')', '') : val.slice(1)) || '—' : val || '—';
          doc.setTextColor(...valColor);
          if (!isFlat) {
            const textW = doc.getTextWidth(valText);
            drawArrow(textCenterX - textW / 2 - aw * 2 - 1, !isNeg, valColor);
          }
          doc.text(valText, textCenterX, yPos, { align: 'center' });
        }
      }
      doc.setTextColor(...textColor);

      const sepTop = tableTop - 4;
      const sepBot = yPos + 2;
      doc.setDrawColor(...textColor);
      doc.setLineWidth(0.1);
      doc.line(margin + labelColW, sepTop, margin + labelColW, sepBot);
      for (let i = 1; i < polymerTrends.length; i++) {
        doc.line(margin + labelColW + colW * i, sepTop, margin + labelColW + colW * i, sepBot);
      }

      yPos += 14;
    }

    doc.setTextColor(...textColor);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize((report.title || 'Market Report').toString(), contentWidth);
    doc.text(titleLines, margin, yPos);
    yPos += titleLines.length * 5 + 5;

    renderSection('Market Overview', report.marketOverviewBody, contentWidth);

    const rightColWidth = Math.max(70, contentWidth * 0.15);
    const leftWidthAvail = contentWidth - rightColWidth - gutter;
    const colWidth = rightColWidth;
    const driverObj = driverMap[abbr];
    if (driverObj) {
      ensureSpace(30);
      const yBlockStart = yPos;
      let spotY = yPos;
      const getRecentFridays = (isRefinery: boolean) => {
        const dayOfWeek = anchorDate.getUTCDay();
        const daysSinceFriday = dayOfWeek >= 5 ? dayOfWeek - 5 : dayOfWeek + 2;
        const lastFriday = new Date(anchorDate);
        lastFriday.setUTCDate(anchorDate.getUTCDate() - daysSinceFriday);
        const fridays: string[] = [];
        if (isRefinery) {
          const twoFridaysAgo = new Date(lastFriday);
          twoFridaysAgo.setUTCDate(lastFriday.getUTCDate() - 7);
          const threeFridaysAgo = new Date(twoFridaysAgo);
          threeFridaysAgo.setUTCDate(twoFridaysAgo.getUTCDate() - 7);
          fridays.push(twoFridaysAgo.toISOString().split('T')[0]);
          fridays.push(threeFridaysAgo.toISOString().split('T')[0]);
        } else {
          const secondLastFriday = new Date(lastFriday);
          secondLastFriday.setUTCDate(lastFriday.getUTCDate() - 7);
          fridays.push(lastFriday.toISOString().split('T')[0]);
          fridays.push(secondLastFriday.toISOString().split('T')[0]);
        }
        return fridays;
      };
      const resultRows: any[] = [];
      const spots = Array.isArray(driverObj.spot) ? driverObj.spot : [];
      for (const spot of spots) {
        for (const feed of spot.feeds) {
          try {
            const isRefinery = String(feed.source).includes('refinery');
            const recentFridays = getRecentFridays(isRefinery);
            const feedResponse = await dataService.getDataByFilename(feed.source);
            const feedData = feedResponse || [];
            if (Array.isArray(feedData) && feedData.length > 0) {
              let filteredData = feedData
                .filter((item: any) => recentFridays.includes(item.date?.split('T')[0]))
                .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());

              if (filteredData.length < 2) {
                const pad = (n: number) => String(n).padStart(2, '0');
                const anchorIso = `${anchorDate.getUTCFullYear()}-${pad(anchorDate.getUTCMonth() + 1)}-${pad(anchorDate.getUTCDate())}`;
                const sorted = [...feedData]
                  .filter((item: any) => (item.date?.split('T')[0] ?? '') <= anchorIso)
                  .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
                filteredData = sorted.slice(0, 2);
              }
              if (filteredData.length > 2) {
                filteredData = filteredData.slice(0, 2);
              }
              const value1 = filteredData[0] && !isNaN(filteredData[0][feed.price])
                ? feed.measure === '%'
                  ? `${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[0][feed.price]))).toFixed(2)}%`
                  : `$${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[0][feed.price]))).toFixed(2)}`
                : null;
              const value2 = filteredData[1] && !isNaN(filteredData[1][feed.price])
                ? feed.measure === '%'
                  ? `${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[1][feed.price]))).toFixed(2)}%`
                  : `$${parseFloat(String(convertValue(feed.mt2lb_convert, filteredData[1][feed.price]))).toFixed(2)}`
                : null;
              const strippedValue1 = value2 ? parseFloat(String(value2).replace(/[$%]/g, '')) : null;
              const strippedValue2 = value1 ? parseFloat(String(value1).replace(/[$%]/g, '')) : null;
              const changeDiff = strippedValue1 !== null && strippedValue2 !== null ? strippedValue2 - strippedValue1 : null;
              const change = changeDiff !== null
                ? feed.measure === '%'
                  ? changeDiff < 0 ? `(${Math.abs(changeDiff).toFixed(2)}%)` : `${changeDiff.toFixed(2)}%`
                  : changeDiff < 0 ? `($${Math.abs(changeDiff).toFixed(2)})` : `$${changeDiff.toFixed(2)}`
                : 'N/A';
              resultRows.push({
                title: spot.title,
                feedName: feed.measure === '%' ? feed.name : `${feed.name} ($/${feed.measure})`,
                measure: feed.measure,
                isRefinery,
                date1: formatDate(recentFridays[1]),
                date2: formatDate(recentFridays[0]),
                value1: value2,
                value2: value1,
                change,
              });
            }
          } catch {}
        }
      }
      const grouped: Record<string, any[]> = {};
      for (const row of resultRows) {
        grouped[row.title] = grouped[row.title] || [];
        grouped[row.title].push(row);
      }
      const titles = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
      doc.setTextColor(...textColor);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      const baseX = margin + leftWidthAvail + gutter;
      const colPositions = [baseX, baseX + 32, baseX + 52, baseX + 78];
      yPos = spotY;
      for (const t of titles) {
        const rows = grouped[t];
        ensureSpace(8);
        spotY = yPos;
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        const groupLabel = rows.every(r => r.isRefinery) ? 'EIA Data' : 'Spot Feeds';
        doc.text(groupLabel, colPositions[0], spotY);
        doc.text(rows[0]?.date1 || '', colPositions[1], spotY);
        doc.text(rows[0]?.date2 || '', colPositions[2], spotY);
        doc.text('Chg', colPositions[3], spotY, { align: 'right' });
        yPos += 3;
        spotY = yPos;
        doc.setDrawColor(...textColor);
        doc.setLineWidth(0.1);
        doc.line(colPositions[0], spotY, colPositions[3], spotY);
        yPos += 4;
        spotY = yPos;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        for (const r of rows) {
          ensureSpace(5);
          spotY = yPos;
          doc.text(r.feedName, colPositions[0], spotY);
          doc.text(r.value1 || '—', colPositions[1], spotY);
          doc.text(r.value2 || '—', colPositions[2], spotY);
          const changeText = r.change !== '$0.00' && r.change !== 'N/A' ? r.change : 'Flat';
          const isNegative = typeof r.change === 'string' && r.change.startsWith('(');
          const isFlat = changeText === 'Flat';
          const changeColor: [number, number, number] = isFlat ? textColor : isNegative ? [232, 91, 76] : [124, 252, 0];
          doc.setTextColor(...changeColor);
          doc.text(changeText, colPositions[3], spotY, { align: 'right' });
          doc.setTextColor(...textColor);
          yPos += 5;
          spotY = yPos;
        }
        yPos += 3;
        spotY = yPos;
      }
      const spotHeight = yPos - yBlockStart;
      yPos += 2;

      const chartsArray: any[] = Array.isArray(driverObj.chart)
        ? driverObj.chart.slice()
        : (driverObj.chart && typeof driverObj.chart === 'object' ? Object.values(driverObj.chart) : []);
      const { default: Chart } = await import('chart.js/auto');
      const chartColumnWidth = colWidth + 10;
      let rightHeight = spotHeight;
      for (const chart of chartsArray) {
        const targetCanvas = document.createElement('canvas');
        const canvasWidthPx = Math.floor(chartColumnWidth * 3.78);
        const canvasHeightPx = 200;
        targetCanvas.width = canvasWidthPx;
        targetCanvas.height = canvasHeightPx;
        targetCanvas.style.position = 'absolute';
        targetCanvas.style.left = '-9999px';
        targetCanvas.style.top = '0';
        targetCanvas.style.visibility = 'hidden';
        const numData = chart.type === 'bar' ? 11 : 4;
        const labels = Array.from({ length: numData }, (_, i) => {
          const month = anchorDate.getUTCMonth() - i;
          const year = month < 0 ? anchorDate.getUTCFullYear() - 1 : anchorDate.getUTCFullYear();
          const adjustedMonth = (month + 12) % 12;
          return new Date(Date.UTC(year, adjustedMonth)).toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
        }).reverse();
        const datasets: any[] = [];
        const lineColors = ['#42b200', '#3838ff', '#854cff', '#e85b4c', '#e880d9'];
        if (chart.values) {
          for (let i = 0; i < chart.values.length; i++) {
            const value = chart.values[i];
            if (value?.source && value.source.length > 2) {
              try {
                const sourceResponse = await dataService.getDataByFilename(value.source);
                const sourceData = sourceResponse || [];
                if (Array.isArray(sourceData) && sourceData.length > 0) {
                  const data = getLastAvailableDays(sourceData, numData).map((item: any) => convertValue(value.mt2lb_convert, item[value.price]));
                  datasets.push({
                    label: value.symbol,
                    data,
                    borderColor: lineColors[i % lineColors.length],
                    backgroundColor: lineColors[i % lineColors.length],
                    fill: false,
                    pointRadius: 1,
                    pointBackgroundColor: lineColors[i % lineColors.length],
                    pointBorderColor: lineColors[i % lineColors.length],
                    yAxisID: chart.dualaxis && value.symbol.includes('BZ') ? 'y2' : 'y1',
                  });
                }
              } catch {}
            }
          }
        }
        if (datasets.length === 0) continue;
        const isBar = chart.type === 'bar';
        const cfg: any = {
          type: isBar ? 'bar' : 'line',
          data: { labels, datasets },
          options: {
            responsive: false,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                display: !isBar,
                labels: !isBar ? {
                  color: '#DEE7D9',
                  usePointStyle: true,
                  pointStyle: 'circle',
                  boxWidth: 6,
                  boxHeight: 6,
                  font: { size: 12, weight: 'normal' },
                  padding: 20,
                } : undefined,
              },
              title: { display: true, text: chart.title, color: '#DEE7D9', font: { size: 14, weight: 'normal' }, padding: { top: 5, bottom: isBar ? 20 : 0 } },
              tooltip: { enabled: false },
              datalabels: { display: false },
            },
            elements: { point: { radius: 0, hitRadius: 5, hoverRadius: 2 } },
            scales: {
              x: { ticks: { color: '#DEE7D9' }, grid: { lineWidth: 1, color: '#DEE7D9', borderDash: [5, 5] } },
              y1: { type: 'linear', position: 'left', ticks: { color: '#DEE7D9', maxTicksLimit: 4, callback: (v: any) => '$' + Number(v).toFixed(2) }, grid: { lineWidth: 1, color: '#DEE7D9', borderDash: [5, 5] } },
              y2: { type: 'linear', position: 'right', display: datasets.some(d => d.yAxisID === 'y2'), ticks: { color: '#DEE7D9', display: datasets.some(d => d.yAxisID === 'y2'), callback: (v: any) => '$' + Number(v).toFixed(2) }, grid: { display: false, drawOnChartArea: false } },
            },
          },
        };
        document.body.appendChild(targetCanvas);
        const chartInstance = new Chart(targetCanvas, cfg);
        await new Promise(resolve => setTimeout(resolve, 100));
        const chartImg = targetCanvas.toDataURL('image/png');
        const chartWidth = chartColumnWidth;
        const chartHeight = Math.min(85, chartWidth * (targetCanvas.height / targetCanvas.width));
        if (chartImg && chartImg.length > 22) {
          const xRight = baseX;
          const yChart = yBlockStart + rightHeight;
          doc.addImage(chartImg, 'PNG', xRight, yChart, chartWidth, chartHeight);
          rightHeight += chartHeight + 3;
        }
        chartInstance.destroy();
        document.body.removeChild(targetCanvas);
      }

      const rightTotal = Math.max(spotHeight, rightHeight);
      const boxX = baseX - 2;
      const boxWidth = chartColumnWidth + 4;
      const boxHeight = rightTotal + 6;
      doc.setDrawColor(...greenColor);
      doc.setLineWidth(0.1);
      doc.rect(boxX, yBlockStart - 5, boxWidth, boxHeight);
      yPos = yBlockStart;
      const leftWidth = Math.max(60, leftWidthAvail);
      renderSection('Supply/Demand', report.supplyDemandBody, leftWidth);
      renderSection('Feedstocks', report.feedstocksBody, leftWidth);
      const afterFeedstocksY = yPos;
      const feedstocksHeight = afterFeedstocksY - yBlockStart;
      const shouldExpandInternational = feedstocksHeight >= rightTotal;
      renderSection('International', report.internationalBody, shouldExpandInternational ? contentWidth : leftWidth);
      const afterInternationalY = yPos;
      const internationalHeight = afterInternationalY - yBlockStart;
      const shouldExpandActionStrategies = internationalHeight >= rightTotal;
      renderSection(`${abbr} Outlook and Action Strategies`, report.actionStrategies, shouldExpandActionStrategies ? contentWidth : leftWidth);
      const leftTotal = yPos - yBlockStart;
      yPos = yBlockStart + Math.max(leftTotal, rightTotal);
      yPos += 6;
    } else {
      renderSection('Supply/Demand', report.supplyDemandBody, contentWidth);
      renderSection('Feedstocks', report.feedstocksBody, contentWidth);
      renderSection('International', report.internationalBody, contentWidth);
      renderSection(`${abbr} Outlook and Action Strategies`, report.actionStrategies, contentWidth);
    }

    doc.setFontSize(8);
    doc.setTextColor(...textColor);
    doc.text(
      `${abbr} - Page ${idx + 2}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  try {
    if (userEmail) {
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(5);
        doc.setTextColor(40, 40, 40);
        doc.text(userEmail, 10, doc.internal.pageSize.getHeight() - 4);
      }
    }
  } catch {}

  return doc.output('blob');
};
